import { FieldType } from '@ngx-formly/material';
import { addDays, addHours, addMonths, addWeeks, format } from 'date-fns';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';

import { Modes } from '../../enums/modes.enum';
import { Styles } from '../../enums/styles.enum';
import { IFsDateRangePickerFormFieldConfig } from '../../interfaces/date-range-picker-form-field-config.interface';
import { IFsDateRangePickerFieldTemplateOptions } from '../../interfaces/date-range-picker-form-field-template-options.interfaces';
import { IRange } from '../../interfaces/range.interface';
import { CustomDateRangeDialogComponent } from '../custom-date-range-dialog/custom-date-range-dialog.component';

@Component({
  selector: 'fs-date-range-picker-v2',
  templateUrl: './date-range-picker-v2.form-field.html',
  styleUrls: ['./date-range-picker-v2.form-field.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FsDateRangePickerV2FormField
  extends FieldType<IFsDateRangePickerFormFieldConfig>
  implements OnDestroy, OnInit
{
  @ViewChild('toggleGroup') public toggleGroup: MatButtonToggleGroup;

  public defaultOptions: IFsDateRangePickerFormFieldConfig = {
    type: 'date-range-picker',
    templateOptions: {
      startTimestampKey: 'event_timestamp__gte',
      endTimestampKey: 'event_timestamp__lte',
      custom: true,
      appearance: 'none',
      style: Styles.Button,
      ranges: [
        { value: '1', mode: Modes.Day, label: '1D' },
        { value: '1', mode: Modes.Week, label: '1W' },
        { value: '2', mode: Modes.Week, label: '2W' },
        { value: '1', mode: Modes.Month, label: '1M' },
      ],
      getRangeMessage: () => {
        return '';
      },
    },
  };

  public ranges: IRange[] = [];
  public extendedRanges: IRange[] = [];
  public startDate: number;
  public endDate: number;
  public dateSelection;
  public searchRangeMessage: string;
  public customDates: any = {};
  private readonly _destroy$ = new Subject<void>();
  public showMoreRanges: boolean = false;

  constructor(private readonly _cd: ChangeDetectorRef, private readonly _matDialog: MatDialog) {
    super();
  }

  public ngOnInit(): void {
    // used to enable resetting buttons from other form fields
    this.templateOptions.clearToggles = this.clearToggles.bind(this);

    this.ranges = this.templateOptions.ranges;
    this.extendedRanges = this.templateOptions.extendedRanges;
    this._patchForm();
    this._cd.detectChanges();
    this.field.formControl.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((value) => {
      const updatedStartDate = new Date(value[this.templateOptions.startTimestampKey] * 1000);
      const updatedEndDate = new Date(value[this.templateOptions.endTimestampKey] * 1000);
      this._updateRangeMessage(updatedStartDate, updatedEndDate);
      this._cd.detectChanges();
    });
    // Setting the default 2 hours query range message
    const start = new Date();
    const startDate = addHours(start, -2);
    const end = new Date();
    this._updateRangeMessage(startDate, end);
    this.startDate = Math.floor(startDate.getTime() / 1000);
    this.endDate = Math.floor(end.getTime() / 1000);
    this._setKeys();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  /**
   * This method is used to clear the button toggle group 'selected' ui state
   */
  public clearToggles(): void {
    if (this.toggleGroup) {
      this.toggleGroup.value = null;
    }
  }

  /**
   * This method is used to change time range through the button toggle group
   * or via opening a custom dialog date range picker
   */
  public onDateSelectionChange(event: any): void {
    if (event.value === 'custom') {
      //
      this.dateSelection = null;
      const ref = this._matDialog.open(CustomDateRangeDialogComponent, {
        data: this.customDates,
      });

      ref
        .afterClosed()
        .pipe(takeUntil(this._destroy$))
        .subscribe((res) => {
          if (res) {
            this.startDate = Math.floor(res.startDate.getTime() / 1000);
            this.endDate = Math.floor(res.endDate.getTime() / 1000);
            this.customDates = {
              startDate: res.startDate,
              endDate: res.endDate,
            };
            this._setKeys();
            this._cd.detectChanges();
            this._updateRangeMessage(res.startDate, res.endDate);
          } else {
            if (Object.keys(this.customDates).length) {
              this._updateRangeMessage(this.customDates.startDate, this.customDates.endDate);
            }
          }
        });
    } else if (event.value !== 'none') {
      const start = new Date();
      let startDate;
      switch (event.value.mode) {
        case Modes.Hour:
          startDate = addHours(start, +-event.value.value);
          this.startDate = Math.floor(startDate.getTime() / 1000);
          break;
        case Modes.Day:
          startDate = addDays(start, +-event.value.value);
          this.startDate = Math.floor(startDate.getTime() / 1000);
          break;
        case Modes.Week:
          startDate = addWeeks(start, +-event.value.value);
          this.startDate = Math.floor(startDate.getTime() / 1000);
          break;
        case Modes.Month:
          startDate = addMonths(start, +-event.value.value);
          this.startDate = Math.floor(startDate.getTime() / 1000);
          break;
      }

      const end = new Date();
      this.endDate = Math.floor(end.getTime() / 1000);
      this._updateRangeMessage(startDate, end);
      this._setKeys();
    }
  }

  /**
   * This method is used to change time range based on the selection
   * in the 'extended' button menu
   */
  public onChangeTimeRangeMenu(value: any): void {
    const start = new Date();
    let startDate;
    switch (value.mode) {
      case Modes.Hour:
        startDate = addHours(start, +-value.value);
        this.startDate = Math.floor(startDate.getTime() / 1000);
        break;
      case Modes.Day:
        startDate = addDays(start, +-value.value);
        this.startDate = Math.floor(startDate.getTime() / 1000);
        break;
      case Modes.Week:
        startDate = addWeeks(start, +-value.value);
        this.startDate = Math.floor(startDate.getTime() / 1000);
        break;
      case Modes.Month:
        startDate = addMonths(start, +-value.value);
        this.startDate = Math.floor(startDate.getTime() / 1000);
        break;
    }

    const end = new Date();
    this.endDate = Math.floor(end.getTime() / 1000);
    this._updateRangeMessage(startDate, end);
    this._setKeys();
  }

  public change(value: any): void {
    this.formControl.setValue(value);
    this.to.change(this.field, value);
  }

  private _setKeys(): void {
    const startKey = this.templateOptions.startTimestampKey;
    const endKey = this.templateOptions.endTimestampKey;

    const value = {};
    value[startKey] = this.startDate;
    value[endKey] = this.endDate;
    this.formControl.setValue(value);
    this.change(value);
  }

  get templateOptions(): IFsDateRangePickerFieldTemplateOptions {
    return this.field.templateOptions;
  }

  get modelSearchRangeMessage(): string {
    return this.templateOptions.getRangeMessage() || '';
  }

  private _patchForm(): void {
    if (this.value) {
      this.customDates.startDate = this.value[this.templateOptions.startTimestampKey];
      this.customDates.endDate = this.value[this.templateOptions.endTimestampKey];
      this.templateOptions.custom = true;
      this._updateRangeMessage(this.customDates.startDate, this.customDates.endDate);
    }
    this._cd.detectChanges();
  }

  private _updateRangeMessage(start: number | Date, end: number | Date): void {
    const formatted = `${format(start, 'MM/dd/yy HH:mm')} - ${format(end, 'MM/dd/yy HH:mm')}`;
    this.searchRangeMessage = formatted;
    this.field.model['searchRangeMessage'] = formatted;

    this.dateSelection = this.searchRangeMessage;
    this._cd.detectChanges();
  }
}
